import type { PlayerId } from "@arcade-hockey/shared";
import type { MatchState } from "../state/types.js";
import { findSeatForPlayer, findTeamByPlayer } from "./seat-control.js";
import { pickNearestToPuck } from "./switching.js";

export function applyAutoSwitchOnPass(state: MatchState): void {
  for (const ev of state.events) {
    if (ev.type !== "PassReleased") continue;
    if (ev.tick !== state.tick) continue;

    const team = findTeamByPlayer(state, ev.from);
    if (team === null) continue;
    const seatId = findSeatForPlayer(team, ev.from);
    if (seatId === undefined) continue;

    const candidates = state.players.filter(
      (p) => p.teamId === team.teamId && p.id !== ev.from && !p.benched,
    );
    const target = pickNearestToPuck(candidates, state.puck.pos);
    if (target === undefined) continue;

    if (seatId === "P1" || seatId === "P2") {
      team.controlledByHuman[seatId] = target.id;
    }
    ev.targetPlayerId = target.id;
  }
}

/**
 * Auto-switch on independent pickup: when the puck changes hands this tick
 * (Loose → Controlled, where the new carrier isn't already the seat owner),
 * snap any human seat on the carrier's team onto the new carrier. Caller
 * passes the puck's owner from before the pickup detection so we can tell a
 * fresh acquisition from a continuing carry.
 *
 * Slice 4's pass-time auto-switch already handles the pass-then-receive case;
 * this covers everything else (faceoff scramble, rebound off a shot, post-
 * strip recovery by a teammate, etc.). Mirrors Slice 4's convention of
 * overriding manual selection — the user can press Poke again to re-pick.
 */
export function applyAutoSwitchOnPickup(state: MatchState, prevOwnerId: PlayerId | null): void {
  const newOwnerId = state.puck.ownerId;
  if (newOwnerId === null) return;
  if (newOwnerId === prevOwnerId) return;
  const carrier = state.playersById.get(newOwnerId);
  if (carrier === undefined) return;
  for (const team of state.control) {
    if (team.teamId !== carrier.teamId) continue;
    // In 3v3 only P1 is occupied; once 2-player local co-op lands this
    // should choose just the closer seat instead of yanking both.
    if (team.controlledByHuman.P1 !== null && team.controlledByHuman.P1 !== carrier.id) {
      team.controlledByHuman.P1 = carrier.id;
    }
    if (team.controlledByHuman.P2 !== null && team.controlledByHuman.P2 !== carrier.id) {
      team.controlledByHuman.P2 = carrier.id;
    }
  }
}
